import React from 'react'
import { Route, createHashRouter, createRoutesFromElements } from 'react-router-dom'

import { DropzoneLayout } from '@documents/contexts/dropzone-context'

import { Layout } from './components/screens/layout'
import { ErrorBoundary } from './components/screens/non-terminal/error/error-boundary'
import { ClientSessionState } from './lib/api/client-session/types'
import { routes, sessionStateRoutes } from './routes'

const imports = {
  // These are needed for the current page, but they aren't required to initialize this module.
  async: {
    ErrorBoundary: import('./components/screens/non-terminal/error/error-boundary'),
    AutoNavigate: import('./components/screens/auto-navigate'),
    Loading: import('./components/screens/non-terminal/loading'),
  },
  // These are not needed for the current page, but may be useful to download for future navigation. These should be
  // roughly ordered by how likely they'll be used as the first (or second) page loaded by the browser.
  prefetch: {
    // By far the most important screen to prefetch.
    getConsent: () => import(/* webpackPrefetch: true */ './components/screens/consent'),
    // TODO: Add some important screens here, which are less important than the consent page but more than the rest.

    // Unordered. These are "the rest".
    getWaitingForInstantData: () =>
      import(/* webpackPrefetch: true */ './components/screens/non-terminal/waiting-for-instant-data'),
    getFakeWaitingForInstantData: () =>
      import(/* webpackPrefetch: true */ './components/screens/non-terminal/fake-waiting-for-instant-data'),
    getConfirmExit: () => import(/* webpackPrefetch: true */ './components/screens/non-terminal/confirm-exit'),
    getEmployerNotFound: () =>
      import(/* webpackPrefetch: true */ './components/screens/non-terminal/employer-not-found'),
    getBankContactInfo: () => import(/* webpackPrefetch: true */ './components/screens/tasks/screens/strategies/bank'),
    getBankIdentifyIncome: () => import('./components/screens/tasks/screens/strategies/bank/identify-income'),
    getBankConnectedTasks: () => import('./components/screens/tasks/screens/strategies/bank/bank-connected-tasks'),
    getFinicityConnect: () =>
      import(/* webpackPrefetch: true */ './components/screens/tasks/screens/strategies/bank/finicity'),
    getStatedInformation: () =>
      import(/* webpackPrefetch: true */ '@frame/components/screens/tasks/screens/stated-information'),
    getCompanySearch: () =>
      import(/* webpackPrefetch: true */ '@frame/components/screens/tasks/screens/company-search'),
    getTasks: () => import(/* webpackPrefetch: true */ './components/screens/tasks'),
    getSelectIncomeType: () =>
      import(/* webpackPrefetch: true */ '@frame/components/screens/tasks/screens/select-income-type'),
    getSelectVerificationMethod: () =>
      import(/* webpackPrefetch: true */ '@frame/components/screens/tasks/screens/select-verification-method'),
    getCredentials: () => import(/* webpackPrefetch: true */ '@credentials/index'),
    getDocuments: () => import(/* webpackPrefetch: true */ '@documents/index'),
    getErrorPage: () => import(/* webpackPrefetch: true */ './components/screens/non-terminal/error'),
    getExit: () => import(/* webpackPrefetch: true */ './components/screens/non-terminal/exit'),
    getFeedback: () => import(/* webpackPrefetch: true */ './components/screens/non-terminal/feedback'),
    getComplete: () => import(/* webpackPrefetch: true */ './components/screens/terminal/complete'),
    getCompleteByOtherStrategy: () =>
      import(/* webpackPrefetch: true */ './components/screens/terminal/complete-by-other-strategy'),
    getExpired: () => import(/* webpackPrefetch: true */ './components/screens/terminal/expired'),
    getFailed: () => import(/* webpackPrefetch: true */ './components/screens/terminal/failed'),
    getFallback: () => import(/* webpackPrefetch: true */ './components/screens/terminal/fallback'),
    getFeedbackComplete: () =>
      import(/* webpackPrefetch: true */ './components/screens/non-terminal/feedback-complete'),
    getConnectionError: () => import(/* webpackPrefetch: true */ '@credentials/connection-error'),
    getConfirmEmployer: () =>
      import(/* webpackPrefetch: true */ '@frame/components/screens/tasks/screens/unified-search/confirm-employer'),
    getCheckPlatform: () =>
      import(/* webpackPrefetch: true */ '@frame/components/screens/tasks/screens/check-platform'),
    getSearchPlatform: () =>
      import(/* webpackPrefetch: true */ '@frame/components/screens/tasks/screens/unified-search/search-platform'),
    getDropzoneContext: () => import(/* webpackPrefetch: true */ '@documents/contexts/dropzone-context'),
    getTaxLink: () => import(/* webpackPrefetch: true */ '@frame/components/screens/tasks/screens/strategies/tax'),
    getAlternativeMethod: () =>
      import(
        /* webpackPrefetch: true */ '@frame/components/screens/tasks/screens/strategies/documents/v2/screens/alternative-methods'
      ),
    getSelectDocumentType: () =>
      import(
        /* webpackPrefetch: true */ '@frame/components/screens/tasks/screens/strategies/documents/v2/screens/select-document-type'
      ),
    getSelectOtherDocumentType: () =>
      import(
        /* webpackPrefetch: true */ '@frame/components/screens/tasks/screens/strategies/documents/v2/screens/select-other-document-type'
      ),
    getUploadDocuments: () =>
      import(
        /* webpackPrefetch: true */ '@frame/components/screens/tasks/screens/strategies/documents/v2/screens/upload-documents'
      ),
  },
}

const lazy = {
  WaitingForInstantData: async () => {
    const { WaitingForInstantData } = await imports.prefetch.getWaitingForInstantData()
    return { element: <WaitingForInstantData /> }
  },
  FakeWaitingForInstantData: async () => {
    const { FakeWaitingForInstantData } = await imports.prefetch.getFakeWaitingForInstantData()
    return { element: <FakeWaitingForInstantData /> }
  },
  ConfirmExit: async () => {
    const { ConfirmExit } = await imports.prefetch.getConfirmExit()
    return { element: <ConfirmExit /> }
  },
  EmployerNotFound: async () => {
    const { EmployerNotFound } = await imports.prefetch.getEmployerNotFound()
    return { element: <EmployerNotFound /> }
  },
  StatedInformation: async () => {
    const { StatedInformation } = await imports.prefetch.getStatedInformation()
    return { element: <StatedInformation /> }
  },
  CompanySearch: async () => {
    const { CompanySearch } = await imports.prefetch.getCompanySearch()
    return { element: <CompanySearch /> }
  },
  Tasks: async () => {
    const { Tasks } = await imports.prefetch.getTasks()
    return { element: <Tasks /> }
  },
  SelectIncomeType: async () => {
    const { SelectIncomeType } = await imports.prefetch.getSelectIncomeType()
    return { element: <SelectIncomeType /> }
  },
  SelectVerificationMethod: async () => {
    const { SelectVerificationMethod } = await imports.prefetch.getSelectVerificationMethod()
    return { element: <SelectVerificationMethod /> }
  },
  Credentials: async () => {
    const { Credentials } = await imports.prefetch.getCredentials()
    return { element: <Credentials /> }
  },
  Documents: async () => {
    const { Documents } = await imports.prefetch.getDocuments()
    return { element: <Documents /> }
  },
  ErrorPage: async () => {
    const { ErrorPage } = await imports.prefetch.getErrorPage()
    return { element: <ErrorPage /> }
  },
  Exit: async () => {
    const { Exit } = await imports.prefetch.getExit()
    return { element: <Exit /> }
  },
  Feedback: async () => {
    const { Feedback } = await imports.prefetch.getFeedback()
    return { element: <Feedback /> }
  },
  Loading: async () => {
    const { Loading } = await imports.async.Loading
    return { element: <Loading /> }
  },
  Complete: async () => {
    const { Complete } = await imports.prefetch.getComplete()
    return { element: <Complete /> }
  },
  CompleteByOtherStrategy: async () => {
    const { CompleteByOtherStrategy } = await imports.prefetch.getCompleteByOtherStrategy()
    return { element: <CompleteByOtherStrategy /> }
  },
  Expired: async () => {
    const { Expired } = await imports.prefetch.getExpired()
    return { element: <Expired /> }
  },
  Failed: async () => {
    const { Failed } = await imports.prefetch.getFailed()
    return { element: <Failed /> }
  },
  Fallback: async () => {
    const { Fallback } = await imports.prefetch.getFallback()
    return { element: <Fallback /> }
  },
  AutoNavigate: async () => {
    const { AutoNavigate } = await imports.async.AutoNavigate
    return { element: <AutoNavigate /> }
  },
  Consent: async () => {
    const { Consent } = await imports.prefetch.getConsent()
    return { element: <Consent /> }
  },
  FeedbackComplete: async () => {
    const { FeedbackComplete } = await imports.prefetch.getFeedbackComplete()
    return { element: <FeedbackComplete /> }
  },
  ConnectionError: async () => {
    const { ConnectionError } = await imports.prefetch.getConnectionError()
    return { element: <ConnectionError /> }
  },
  ConfirmEmployer: async () => {
    const { ConfirmEmployer } = await imports.prefetch.getConfirmEmployer()

    return { element: <ConfirmEmployer /> }
  },
  CheckPlatform: async () => {
    const { CheckPlatform } = await imports.prefetch.getCheckPlatform()
    return { element: <CheckPlatform /> }
  },
  SearchPlatform: async () => {
    const { SearchPlatform } = await imports.prefetch.getSearchPlatform()
    return {
      element: <SearchPlatform />,
    }
  },
  BankStatedInfo: async () => {
    const { BankContactInfo } = await imports.prefetch.getBankContactInfo()
    return {
      element: <BankContactInfo />,
    }
  },
  FinicityConnect: async () => {
    const { FinicityConnectWidget } = await imports.prefetch.getFinicityConnect()
    return {
      element: <FinicityConnectWidget />,
    }
  },
  BankIdentifyIncome: async () => {
    const { BankIdentifyIncome } = await imports.prefetch.getBankIdentifyIncome()
    return {
      element: <BankIdentifyIncome />,
    }
  },
  BankConnectedTasks: async () => {
    const { BankConnectedTasks } = await imports.prefetch.getBankConnectedTasks()
    return {
      element: <BankConnectedTasks />,
    }
  },
  TaxLink: async () => {
    const { TaxLink } = await imports.prefetch.getTaxLink()
    return { element: <TaxLink /> }
  },
  AlternativeMethods: async () => {
    const { AlternativeMethods } = await imports.prefetch.getAlternativeMethod()
    return { element: <AlternativeMethods /> }
  },
  SelectDocumentType: async () => {
    const { SelectDocumentType } = await imports.prefetch.getSelectDocumentType()
    return { element: <SelectDocumentType /> }
  },
  SelectOtherDocumentType: async () => {
    const { SelectOtherDocumentType } = await imports.prefetch.getSelectOtherDocumentType()
    return { element: <SelectOtherDocumentType /> }
  },
  UploadDocuments: async () => {
    const { UploadDocuments } = await imports.prefetch.getUploadDocuments()
    return { element: <UploadDocuments /> }
  },
}

const sessionStateElement: Record<ClientSessionState, () => Promise<{ element: JSX.Element }>> = {
  [ClientSessionState.WAITING_FOR_INSTANT_DATA]: lazy.WaitingForInstantData,
  [ClientSessionState.CONSENT]: lazy.Consent,
  [ClientSessionState.EMPLOYMENTS]: lazy.Tasks,
  [ClientSessionState.INITIALIZED]: lazy.Loading,
  [ClientSessionState.TERMINAL_COMPLETE]: lazy.Complete,
  [ClientSessionState.TERMINAL_COMPLETE_BY_OTHER_STRATEGY]: lazy.CompleteByOtherStrategy,
  [ClientSessionState.TERMINAL_EXPIRED]: lazy.Expired,
  [ClientSessionState.TERMINAL_FALLBACK]: lazy.Fallback,
  [ClientSessionState.TERMINAL_FAILED]: lazy.Failed,
}

function createRouteWithTaskId(path: string, lazyComponent: () => Promise<{ element: JSX.Element }>) {
  return <Route key={path} path={`${path}/:taskId?`} lazy={lazyComponent} />
}

export const router = createHashRouter(
  createRoutesFromElements(
    <>
      <Route element={<DropzoneLayout />}>
        <Route element={<Layout />} errorElement={<ErrorBoundary />}>
          <Route path={routes.root} lazy={lazy.AutoNavigate}>
            {Object.values(ClientSessionState).map((state) => (
              <Route key={state} path={sessionStateRoutes[state]} lazy={sessionStateElement[state]} />
            ))}
          </Route>
          <Route path={routes.complete} lazy={lazy.Complete} />
          <Route path={routes.confirmExit} lazy={lazy.ConfirmExit} />
          <Route path={routes.employerNotFound} lazy={lazy.EmployerNotFound} />
          <Route path={routes.feedback} lazy={lazy.Feedback} />
          <Route path={routes.exit} lazy={lazy.Exit} />
          <Route path={routes.fakeWaitingForInstant} lazy={lazy.FakeWaitingForInstantData} />
          <Route path={routes.error} lazy={lazy.ErrorPage} />
          <Route path={routes.unauthorized} lazy={lazy.Expired} />
          <Route path={routes.feedbackComplete} lazy={lazy.FeedbackComplete} />
          <Route path={routes.selectIncomeType} lazy={lazy.SelectIncomeType} />
          {createRouteWithTaskId(routes.selectVerificationMethod, lazy.SelectVerificationMethod)}
          {createRouteWithTaskId(routes.taxLink, lazy.TaxLink)}
          {createRouteWithTaskId(routes.companySearch, lazy.CompanySearch)}
          {createRouteWithTaskId(routes.statedInformation, lazy.StatedInformation)}
          {createRouteWithTaskId(routes.bankStatedInformation, lazy.BankStatedInfo)}
          {createRouteWithTaskId(routes.finicityConnect, lazy.FinicityConnect)}
          {createRouteWithTaskId(routes.bankIdentifyIncome, lazy.BankIdentifyIncome)}
          {createRouteWithTaskId(routes.bankConnectedTasks, lazy.BankConnectedTasks)}
          {createRouteWithTaskId(routes.credentials, lazy.Credentials)}
          {createRouteWithTaskId(routes.connectionError, lazy.ConnectionError)}
          {createRouteWithTaskId(routes.platformSearch, lazy.SearchPlatform)}
          {createRouteWithTaskId(routes.platformCheck, lazy.CheckPlatform)}
          {createRouteWithTaskId(routes.confirmEmployer, lazy.ConfirmEmployer)}
          {createRouteWithTaskId(routes.documents, lazy.Documents)}
          {createRouteWithTaskId(routes.alternativeMethods, lazy.AlternativeMethods)}
          {createRouteWithTaskId(routes.selectDocumentType, lazy.SelectDocumentType)}
          {createRouteWithTaskId(routes.selectOtherDocumentType, lazy.SelectOtherDocumentType)}
          {createRouteWithTaskId(routes.uploadDocuments, lazy.UploadDocuments)}
        </Route>
      </Route>
    </>
  ),
  {
    basename: routes.root,
  }
)
